/* ===========================================================================
   sollie-ar.js — Sollie PDP v2 "See it in your space" pseudo-AR viewer.
   Additive, dependency-free. Ported from research/ar-skeleton/index.html
   (verified skeleton: drag/pinch/rotate, border-flood white-keying with
   de-fringe, fixture guides, snapshot, photo fallback) with theme lifecycle
   hardening:
   - Modal is built ONCE at BODY level on first open, so Horizon's variant-
     change section morphs can never strip it. The open trigger is a
     delegated document-level click on [data-sv2-ar-open]; re-rendered PDP
     buttons keep working, and image/dims are read fresh from the clicked
     button's data attributes every open.
   - Scroll lock = body position:fixed + reflow-before-restore. Never
     html{overflow:hidden} (it snaps scrollY to 0 and fights sticky headers).
   - history.pushState('#ar') so the back button/gesture closes the modal
     instead of leaving the PDP.
   - Camera: getUserMedia called synchronously inside the tap gesture;
     playsinline/muted/autoplay live as ATTRIBUTES in the markup before
     srcObject is assigned; sticky denial lands softly in photo mode;
     visibilitychange pauses/resumes the stream; pagehide stops tracks;
     pageshow (bfcache) self-heals a dead stream.
   - Canvas caps: keyed product canvas <=1200px long edge, snapshot canvas
     <=1600px, snapshot canvas released right after toBlob (iOS memory pool).
   Analytics: document CustomEvent 'sollie:ar' with detail.action one of
   open | mode | place | snapshot | share | denied.
   ======================================================================== */
(function () {
  'use strict';
  if (window.__sv2AR) return;
  window.__sv2AR = true;

  var MAX_KEY = 1200;      /* keyed product canvas long-edge cap (px)   */
  var MAX_SNAP = 1600;     /* snapshot canvas long-edge cap (px)        */
  var T_LOW = 225;         /* whiteness flood threshold (drop to ~210 for grey-gradient sweeps) */
  var T_HIGH = 247;        /* fully-transparent whiteness threshold     */
  var CEIL_Y = 0.10, SURF_Y = 0.68;
  var HINTS = {
    hanging: 'Drag to hang it. Pinch to resize.',
    flush: 'Line the top up with your ceiling.',
    wall: 'Hold your phone square to the wall.',
    surface: 'Rest the base on the surface line.',
    free: 'Drag to place. Pinch to resize.'
  };

  var st = {
    open: false, mode: null, stream: null,
    src: '', cutoutSrc: '', loadedUrl: '', precut: false,
    rawImg: null, keyed: null, cutout: true,
    imgW: 0, imgH: 0,      /* overlay natural px */
    x: 0, y: 0,            /* overlay CENTER in stage px */
    scale: 1, rot: 0,
    dims: {}, profile: 'free',
    trigger: null, sY: 0, snapBusy: false, toastUrl: null,
    placed: false, dragMoved: false
  };
  var ui = null;

  /* ---------------- helpers ---------------- */
  function emit(action, detail) {
    document.dispatchEvent(new CustomEvent('sollie:ar', { detail: Object.assign({ action: action }, detail || {}) }));
  }
  function profileFor(t) {
    t = String(t || '').toLowerCase();
    if (/pendant|chandelier/.test(t)) return 'hanging';
    if (/ceiling/.test(t)) return 'flush';
    if (/wall|sconce/.test(t)) return 'wall';
    if (/table/.test(t)) return 'surface';
    return 'free';                                  /* floor + unknown = free */
  }

  /* Dimension parser (spec 2.6) — raw variant option strings in, cm out. */
  function parseDims(str) {
    if (!str) return {};
    var out = {};
    var rest = str;
    /* (?:^|[^a-z]) guards keep the h in "Width"/the d in "Standard" from matching */
    var m = str.match(/(?:Ø|∅|⌀|(?:^|[^a-z])D(?:ia(?:meter)?)?\.?\s*:?\s*)(\d{1,3}(?:[.,]\d)?)\s*cm/i);
    if (m) out.w = parseFloat(m[1].replace(',', '.'));
    m = str.match(/(?:^|[^a-z])(H(?:eight)?\.?\s*:?\s*(\d{1,3}(?:[.,]\d)?)\s*cm)/i);
    if (m) { out.h = parseFloat(m[2].replace(',', '.')); rest = str.replace(m[1], ''); }
    if (out.w == null) {
      m = rest.match(/\b(\d{1,3}(?:[.,]\d)?)\s*cm\b/i);        /* bare "40cm" (height text stripped) */
      if (m) out.w = parseFloat(m[1].replace(',', '.'));
    }
    m = str.match(/(\d{1,3}(?:[.,]\d)?)\s*(?:"|in\b)/i);        /* inches, future-proof */
    if (m && out.w == null) out.w = parseFloat(m[1].replace(',', '.')) * 2.54;
    return out;
  }

  /* White-keying (spec 3.1): border-connected flood + feather + de-fringe.
     The flood-from-borders step is the lampshade protector: a white shade
     fully enclosed by the lamp silhouette is never border-connected, so it
     stays opaque no matter how white it is. Returns null on CORS taint. */
  function keyWhite(img) {
    var s = Math.min(1, MAX_KEY / Math.max(img.naturalWidth, img.naturalHeight));
    var W = Math.max(1, Math.round(img.naturalWidth * s));
    var H = Math.max(1, Math.round(img.naturalHeight * s));
    var c = document.createElement('canvas'); c.width = W; c.height = H;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);
    var id;
    try { id = ctx.getImageData(0, 0, W, H); } catch (e) { return null; }
    var d = id.data;

    var n = W * H;
    var wht = new Float32Array(n);                  /* whiteness score */
    for (var i = 0; i < n; i++) {
      var r = d[i * 4], g = d[i * 4 + 1], b = d[i * 4 + 2];
      var L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      var C = Math.max(r, g, b) - Math.min(r, g, b);
      wht[i] = L - 0.9 * C;                         /* chroma penalty keeps warm glow */
    }
    var bg = new Uint8Array(n), stack = [];
    function seed(p) { if (!bg[p] && wht[p] >= T_LOW) { bg[p] = 1; stack.push(p); } }
    for (var x = 0; x < W; x++) { seed(x); seed((H - 1) * W + x); }
    for (var y = 0; y < H; y++) { seed(y * W); seed(y * W + W - 1); }
    while (stack.length) {
      var p = stack.pop(), px = p % W, py = (p - px) / W;
      if (px > 0) seed(p - 1);
      if (px < W - 1) seed(p + 1);
      if (py > 0) seed(p - W);
      if (py < H - 1) seed(p + W);
    }
    for (i = 0; i < n; i++) {
      if (!bg[i]) continue;
      var wv = wht[i], a;
      if (wv >= T_HIGH) a = 0;
      else if (wv <= T_LOW) a = 255;
      else a = Math.round(255 * (T_HIGH - wv) / (T_HIGH - T_LOW));
      d[i * 4 + 3] = a;
      if (a > 0 && a < 255) {                       /* un-mix from white (kills halo on dark rooms) */
        var af = a / 255;
        for (var ch = 0; ch < 3; ch++) {
          d[i * 4 + ch] = Math.max(0, Math.min(255, Math.round((d[i * 4 + ch] - (1 - af) * 255) / af)));
        }
      }
    }
    ctx.putImageData(id, 0, 0);
    return c;
  }

  /* ---------------- modal shell (built once, at body level) ---------------- */
  function buildModal() {
    if (ui) return;
    var root = document.createElement('div');
    root.className = 'sv2-ar-modal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'See it in your space');
    root.innerHTML =
      '<div class="sv2-ar-stage">' +
        '<video class="sv2-ar-video" playsinline muted autoplay hidden></video>' +
        '<img class="sv2-ar-bg" alt="" hidden>' +
        '<svg class="sv2-ar-guides" aria-hidden="true"></svg>' +
        '<div class="sv2-ar-overlay" aria-hidden="true"></div>' +
        '<div class="sv2-ar-scalebar" hidden><span class="sv2-ar-scalebar__bar"></span><span class="sv2-ar-scalebar__lbl">About 1 ft / 30 cm</span></div>' +
      '</div>' +
      '<div class="sv2-ar-topbar">' +
        '<span class="sv2-ar-chip sv2-ar-chip--dims" hidden></span>' +
        '<span class="sv2-ar-chip sv2-ar-chip--mode"></span>' +
        '<button type="button" class="sv2-ar-close" aria-label="Close">&#10005;</button>' +
      '</div>' +
      '<div class="sv2-ar-bottombar">' +
        '<p class="sv2-ar-hint"></p>' +
        '<div class="sv2-ar-controls">' +
          '<button type="button" class="sv2-ar-btn sv2-ar-btn--camera" aria-pressed="false">Use camera</button>' +
          '<button type="button" class="sv2-ar-shutter" aria-label="Take a snapshot"></button>' +
          '<button type="button" class="sv2-ar-btn sv2-ar-btn--photo" aria-pressed="false">Use a photo</button>' +
          '<button type="button" class="sv2-ar-btn sv2-ar-btn--cutout" aria-pressed="true">Cutout on</button>' +
        '</div>' +
      '</div>' +
      '<p class="sv2-ar-msg" role="status" hidden></p>' +
      '<div class="sv2-ar-flash" aria-hidden="true"></div>' +
      '<div class="sv2-ar-toast" hidden>' +
        '<img class="sv2-ar-toast__img" alt="Snapshot preview">' +
        '<button type="button" class="sv2-ar-btn sv2-ar-toast__share" hidden>Share</button>' +
        '<a class="sv2-ar-btn sv2-ar-toast__save" download="sollie-in-my-space.jpg">Save</a>' +
        '<button type="button" class="sv2-ar-btn sv2-ar-toast__close" aria-label="Dismiss">&#10005;</button>' +
      '</div>' +
      '<input type="file" class="sv2-ar-file" accept="image/*" hidden>';
    document.body.appendChild(root);
    var q = function (sel) { return root.querySelector(sel); };
    ui = {
      root: root,
      stage: q('.sv2-ar-stage'), video: q('.sv2-ar-video'), bg: q('.sv2-ar-bg'),
      guides: q('.sv2-ar-guides'), overlay: q('.sv2-ar-overlay'),
      scalebar: q('.sv2-ar-scalebar'), bar: q('.sv2-ar-scalebar__bar'),
      chipDims: q('.sv2-ar-chip--dims'), chipMode: q('.sv2-ar-chip--mode'),
      close: q('.sv2-ar-close'), hint: q('.sv2-ar-hint'),
      btnCam: q('.sv2-ar-btn--camera'), btnPhoto: q('.sv2-ar-btn--photo'),
      btnCut: q('.sv2-ar-btn--cutout'), shutter: q('.sv2-ar-shutter'),
      msg: q('.sv2-ar-msg'), flash: q('.sv2-ar-flash'),
      toast: q('.sv2-ar-toast'), toastImg: q('.sv2-ar-toast__img'),
      toastShare: q('.sv2-ar-toast__share'), toastSave: q('.sv2-ar-toast__save'),
      toastClose: q('.sv2-ar-toast__close'), file: q('.sv2-ar-file')
    };
    wire();
  }

  function showMsg(t, ms) {
    ui.msg.textContent = t; ui.msg.hidden = false;
    clearTimeout(showMsg._t);
    if (ms) showMsg._t = setTimeout(function () { ui.msg.hidden = true; }, ms);
  }
  function hideMsg() { if (ui) ui.msg.hidden = true; }
  function hideToast() {
    if (!ui) return;
    ui.toast.hidden = true;
    if (st.toastUrl) { URL.revokeObjectURL(st.toastUrl); st.toastUrl = null; }
  }

  /* ---------------- product overlay ---------------- */
  function loadProduct(cb) {
    if (st.rawImg) return cb();
    var url = st.precut ? st.cutoutSrc : st.src;
    var img = new Image();
    img.crossOrigin = 'anonymous';                  /* Shopify CDN sends CORS headers */
    img.onload = function () {
      st.rawImg = img;
      st.keyed = st.precut ? null : keyWhite(img);  /* precut PNG is already transparent */
      buildOverlay();
      cb();
    };
    img.onerror = function () {                     /* CORS-blocked host: retry un-CORSed, degrade */
      var plain = new Image();
      plain.onload = function () { st.rawImg = plain; st.keyed = null; buildOverlay(); cb(); };
      plain.onerror = function () { showMsg('Could not load the product image.', 4000); };
      plain.src = url;
    };
    img.src = url;
  }
  function buildOverlay() {
    ui.overlay.innerHTML = '';
    ui.overlay.classList.remove('sv2-ar-overlay--multiply');
    if (st.precut) {
      ui.overlay.appendChild(st.rawImg.cloneNode());
      st.imgW = st.rawImg.naturalWidth; st.imgH = st.rawImg.naturalHeight;
    } else if (st.cutout && st.keyed) {
      ui.overlay.appendChild(st.keyed);
      st.imgW = st.keyed.width; st.imgH = st.keyed.height;
    } else {
      ui.overlay.classList.add('sv2-ar-overlay--multiply');   /* option (a) fallback / cutout-off */
      ui.overlay.appendChild(st.rawImg.cloneNode());
      st.imgW = st.rawImg.naturalWidth; st.imgH = st.rawImg.naturalHeight;
    }
    ui.overlay.style.width = st.imgW + 'px';
    ui.overlay.style.height = st.imgH + 'px';
  }

  /* ---------------- transform + guides + scale bar ---------------- */
  function stageRect() { return ui.stage.getBoundingClientRect(); }
  function initialPlace() {
    var r = stageRect();
    var target = r.width * 0.45;
    st.scale = target / st.imgW;
    st.rot = 0;
    st.x = r.width / 2;
    st.y = st.profile === 'hanging' ? r.height * 0.34
        : st.profile === 'flush' ? r.height * 0.22
        : st.profile === 'surface' ? r.height * 0.52
        : r.height * 0.45;
    applyTransform();
  }
  function applyTransform() {
    /* transform-origin = element center; translate by UNSCALED half-size so the
       element center lands exactly at (st.x, st.y) for any scale/rotation */
    ui.overlay.style.transform =
      'translate(' + (st.x - st.imgW / 2) + 'px,' + (st.y - st.imgH / 2) + 'px)' +
      ' rotate(' + st.rot + 'deg) scale(' + st.scale + ')';
    ui.overlay.style.transformOrigin = (st.imgW / 2) + 'px ' + (st.imgH / 2) + 'px';
    drawGuides();
    drawScalebar();
  }
  function drawGuides() {
    var r = stageRect(), W = r.width, H = r.height, s = '';
    var line = 'stroke="#fff" stroke-width="1.5" stroke-dasharray="7 6" opacity="0.8"';
    var label = 'fill="#fff" font-size="10" opacity="0.85" font-family="sans-serif"';
    if (st.profile === 'hanging' || st.profile === 'flush') {
      var cy = H * CEIL_Y;
      s += '<line x1="0" y1="' + cy + '" x2="' + W + '" y2="' + cy + '" ' + line + '/>';
      s += '<text x="10" y="' + (cy - 6) + '" ' + label + '>Ceiling</text>';
      if (st.profile === 'hanging') {               /* drawn cord: ceiling to overlay top-center */
        var topY = st.y - (st.imgH * st.scale) / 2;
        if (topY > cy) s += '<line x1="' + st.x + '" y1="' + cy + '" x2="' + st.x + '" y2="' + (topY + 4) + '" stroke="#e8e2d8" stroke-width="2" opacity="0.65"/>';
      }
    } else if (st.profile === 'wall') {
      s += '<line x1="' + (W * 0.18) + '" y1="' + (H * 0.12) + '" x2="' + (W * 0.18) + '" y2="' + (H * 0.32) + '" ' + line + '/>';
      s += '<line x1="' + (W * 0.82) + '" y1="' + (H * 0.12) + '" x2="' + (W * 0.82) + '" y2="' + (H * 0.32) + '" ' + line + '/>';
      s += '<text x="' + (W * 0.18 + 8) + '" y="' + (H * 0.12 + 12) + '" ' + label + '>Flat wall</text>';
    } else if (st.profile === 'surface') {
      var sy = H * SURF_Y;
      s += '<line x1="0" y1="' + sy + '" x2="' + W + '" y2="' + sy + '" ' + line + '/>';
      s += '<text x="10" y="' + (sy - 6) + '" ' + label + '>Surface</text>';
    }
    ui.guides.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
    ui.guides.innerHTML = s;
  }
  function drawScalebar() {
    /* 1-ft reference, only when real dims parsed; width preferred, height fallback */
    var wPx = st.imgW * st.scale, hPx = st.imgH * st.scale, ftPx = null;
    if (st.dims.w) ftPx = wPx * 30.48 / st.dims.w;
    else if (st.dims.h) ftPx = hPx * 30.48 / st.dims.h;
    if (!ftPx || !isFinite(ftPx)) { ui.scalebar.hidden = true; return; }
    var r = stageRect();
    ui.scalebar.hidden = false;
    ui.bar.style.width = Math.max(8, Math.min(ftPx, r.width * 0.9)) + 'px';
    ui.scalebar.style.left = Math.max(8, st.x - wPx / 2) + 'px';
    ui.scalebar.style.top = Math.min(r.height - 40, st.y + hPx / 2 + 10) + 'px';
  }
  function snapOnRelease() {
    /* release-based gentle snap: flush top edge -> ceiling, surface bottom -> line */
    var r = stageRect(), hHalf = (st.imgH * st.scale) / 2;
    if (st.profile === 'flush') {
      var cy = r.height * CEIL_Y, top = st.y - hHalf;
      if (Math.abs(top - cy) < 24) st.y = cy + hHalf;
    } else if (st.profile === 'surface') {
      var sy = r.height * SURF_Y, bot = st.y + hHalf;
      if (Math.abs(bot - sy) < 24) st.y = sy - hHalf;
    }
    applyTransform();
  }
  var fadeT;
  function guidesAwake() {
    ui.guides.classList.remove('is-faded');
    clearTimeout(fadeT);
    fadeT = setTimeout(function () { ui.guides.classList.add('is-faded'); }, 2500);
  }

  /* ---------------- camera (spec section 4 iOS handling) ---------------- */
  function startCamera() {                          /* call inside a user gesture */
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) { cameraFailed({ name: 'NotFoundError' }); return; }
    var p;
    try {
      p = navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false
      });
    } catch (e) { cameraFailed(e); return; }
    p.then(function (stream) {
      if (!st.open) { stream.getTracks().forEach(function (t) { t.stop(); }); return; }
      st.stream = stream;
      ui.video.srcObject = stream;                  /* playsinline/muted/autoplay already set as attributes */
      ui.video.hidden = false; ui.bg.hidden = true;
      return ui.video.play();
    }).then(function () {
      if (st.open && st.stream) { setMode('camera'); hideMsg(); }
    }).catch(function (err) {
      if (err && (err.name === 'OverconstrainedError' || err.name === 'NotFoundError')) {
        navigator.mediaDevices.getUserMedia({ video: true, audio: false })    /* bare retry: front cam beats nothing */
          .then(function (s2) {
            if (!st.open) { s2.getTracks().forEach(function (t) { t.stop(); }); return; }
            st.stream = s2; ui.video.srcObject = s2;
            ui.video.hidden = false; ui.bg.hidden = true;
            return ui.video.play();
          })
          .then(function () { if (st.open && st.stream) { setMode('camera'); hideMsg(); } })
          .catch(cameraFailed);
      } else cameraFailed(err);
    });
  }
  function cameraFailed(err) {
    emit('denied', { error: err && err.name });
    stopCamera();
    if (!st.open) return;
    showMsg((err && err.name === 'NotAllowedError')
      ? 'Camera access is blocked for this site. Use a photo of your room instead, or re-enable the camera in your browser settings.'
      : 'Camera unavailable. Use a photo of your room instead.', 5000);
    setMode('photo');
    if (!ui.bg.src) ui.file.click();                /* usually still in the gesture chain; harmless if blocked */
  }
  function stopCamera() {
    if (st.stream) { st.stream.getTracks().forEach(function (t) { t.stop(); }); st.stream = null; }
    if (ui) { ui.video.srcObject = null; ui.video.hidden = true; }
  }

  /* ---------------- photo mode ---------------- */
  function usePhotoFile(f) {
    if (!f || !/^image\//.test(f.type)) return;
    var url = URL.createObjectURL(f);
    ui.bg.onload = function () { URL.revokeObjectURL(url); };
    ui.bg.src = url;
    stopCamera();
    ui.bg.hidden = false;
    setMode('photo'); hideMsg();
  }
  function setMode(m) {
    st.mode = m;
    ui.chipMode.textContent = m === 'camera' ? 'Live camera' : 'Room photo';
    ui.btnCam.setAttribute('aria-pressed', String(m === 'camera'));
    ui.btnPhoto.setAttribute('aria-pressed', String(m === 'photo'));
    emit('mode', { mode: m });
  }

  /* ---------------- snapshot (guides/UI excluded) ---------------- */
  function coverDraw(ctx, src, sw, sh, W, H) {      /* same crop as CSS object-fit: cover */
    var s = Math.max(W / sw, H / sh);
    var dw = sw * s, dh = sh * s;
    ctx.drawImage(src, (W - dw) / 2, (H - dh) / 2, dw, dh);
  }
  function snapshot() {
    if (st.snapBusy || !st.rawImg) return;
    var r = stageRect(), src, srcW, srcH;
    if (st.mode === 'camera' && ui.video.videoWidth) { src = ui.video; srcW = ui.video.videoWidth; srcH = ui.video.videoHeight; }
    else if (ui.bg.src && ui.bg.naturalWidth) { src = ui.bg; srcW = ui.bg.naturalWidth; srcH = ui.bg.naturalHeight; }
    else { showMsg('Start the camera or add a room photo first.', 2500); return; }
    st.snapBusy = true;

    var k = Math.min(MAX_SNAP / Math.max(r.width, r.height), 3);
    var W = Math.round(r.width * k), H = Math.round(r.height * k);
    var out = document.createElement('canvas'); out.width = W; out.height = H;
    var ctx = out.getContext('2d');
    coverDraw(ctx, src, srcW, srcH, W, H);

    ctx.save();
    ctx.translate(st.x * k, st.y * k);
    ctx.rotate(st.rot * Math.PI / 180);
    ctx.scale(st.scale * k, st.scale * k);
    var useKeyed = !st.precut && st.cutout && st.keyed;
    var ov = useKeyed ? st.keyed : st.rawImg;
    if (!useKeyed && !st.precut) ctx.globalCompositeOperation = 'multiply';
    try { ctx.drawImage(ov, -st.imgW / 2, -st.imgH / 2); } catch (e) {}
    ctx.restore();

    ui.flash.style.opacity = '0.9';
    setTimeout(function () { ui.flash.style.opacity = '0'; }, 120);
    if (navigator.vibrate) { try { navigator.vibrate(30); } catch (e) {} }

    var done = function (blob) {
      st.snapBusy = false;
      out.width = 0; out.height = 0; out = null;    /* release iOS canvas memory eagerly */
      if (!blob) { showMsg('Snapshot failed. The product image is blocked for canvas use.', 3000); return; }
      emit('snapshot');
      hideToast();
      st.toastUrl = URL.createObjectURL(blob);
      ui.toastImg.src = st.toastUrl;
      ui.toastSave.href = st.toastUrl;
      var file = new File([blob], 'sollie-in-my-space.jpg', { type: 'image/jpeg' });
      var canShare = navigator.canShare && navigator.canShare({ files: [file] });
      ui.toastShare.hidden = !canShare;
      ui.toastShare.onclick = function () {
        navigator.share({ files: [file] }).then(function () { emit('share'); }).catch(function () {});
      };
      ui.toast.hidden = false;
    };
    try { out.toBlob(done, 'image/jpeg', 0.9); }
    catch (e) {                                     /* tainted canvas throws synchronously */
      st.snapBusy = false; out.width = 0; out.height = 0; out = null;
      showMsg('Snapshot failed. The product image is blocked for canvas use.', 3000);
    }
  }

  /* ---------------- scroll lock: body-fixed + reflow-before-restore ---------------- */
  function lockScroll() {
    st.sY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--sv2-sbw', (sbw > 0 ? sbw : 0) + 'px');
    document.body.style.top = -st.sY + 'px';
    document.body.classList.add('sv2-ar-lock');
  }
  function unlockScroll() {
    document.body.classList.remove('sv2-ar-lock');
    document.body.style.top = '';
    document.body.style.removeProperty('--sv2-sbw');
    void document.body.offsetHeight;                /* reflow BEFORE restore or scroll clamps to 0 */
    window.scrollTo(0, st.sY);
  }

  /* ---------------- open / close ---------------- */
  function updateDimChip() {
    var cm = st.dims.w || st.dims.h || null, t = '';
    if (cm) {
      var inches = Math.round(cm / 2.54 * 10) / 10;
      t = 'Approx. ' + inches + ' in / ' + (Math.round(cm * 10) / 10) + ' cm ' + (st.dims.w ? 'wide' : 'tall');
    }
    ui.chipDims.hidden = !t;
    ui.chipDims.textContent = t;
  }
  function open(btn) {
    if (st.open) return;
    buildModal();
    var d = btn.dataset;
    st.src = d.arImage || '';
    st.cutoutSrc = d.arCutout || '';
    st.precut = !!st.cutoutSrc;
    var eff = st.cutoutSrc || st.src;
    if (st.loadedUrl !== eff) {                     /* variant/image changed since last open */
      st.rawImg = null; st.keyed = null; st.loadedUrl = eff;
    }
    st.profile = profileFor(d.arType);
    st.dims = parseDims(d.arDims || '');
    st.cutout = true;
    st.trigger = btn;
    btn.setAttribute('aria-expanded', 'true');
    st.open = true; st.placed = false;

    ui.root.classList.add('is-open');
    lockScroll();
    try { history.pushState({ sv2Ar: 1 }, '', '#ar'); } catch (e) {}
    emit('open', { type: d.arType, profile: st.profile });

    updateDimChip();
    ui.hint.textContent = HINTS[st.profile];
    ui.btnCut.hidden = st.precut;
    ui.btnCut.setAttribute('aria-pressed', 'true');
    ui.btnCut.textContent = 'Cutout on';
    ui.close.focus();

    /* camera attempt stays INSIDE the tap gesture (iOS); desktop goes photo */
    var isTouch = matchMedia('(pointer: coarse)').matches;
    if (isTouch && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) startCamera();
    else { setMode('photo'); if (!ui.bg.src) showMsg('Add a photo of your room to begin.', 3500); }

    loadProduct(function () {
      if (!st.open) return;
      ui.btnCut.hidden = st.precut || !st.keyed;    /* toggle is pointless without a keyed canvas */
      initialPlace();
      guidesAwake();
    });
  }
  function close(fromPop) {
    if (!st.open) return;
    st.open = false;
    ui.root.classList.remove('is-open');
    stopCamera();
    hideToast(); hideMsg();
    unlockScroll();
    if (!fromPop && history.state && history.state.sv2Ar) { try { history.back(); } catch (e) {} }
    if (st.trigger) {
      st.trigger.setAttribute('aria-expanded', 'false');
      if (document.contains(st.trigger)) { try { st.trigger.focus(); } catch (e) {} }
    }
  }
  function trapTab(e) {
    var all = ui.root.querySelectorAll('.sv2-ar-btn,.sv2-ar-close,.sv2-ar-shutter,.sv2-ar-toast__save');
    var list = [];
    for (var i = 0; i < all.length; i++) {
      if (!all[i].hidden && all[i].offsetParent !== null) list.push(all[i]);
    }
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    else if (!ui.root.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
  }

  /* ---------------- modal wiring (elements are ours, never morphed) ---------------- */
  function wire() {
    ui.close.addEventListener('click', function () { close(false); });
    ui.btnCam.addEventListener('click', function () { startCamera(); });
    ui.btnPhoto.addEventListener('click', function () { ui.file.click(); });
    ui.shutter.addEventListener('click', snapshot);
    ui.btnCut.addEventListener('click', function () {
      st.cutout = !st.cutout;
      ui.btnCut.setAttribute('aria-pressed', String(st.cutout));
      ui.btnCut.textContent = st.cutout ? 'Cutout on' : 'Cutout off';
      var cx = st.x, cy = st.y, sc = st.scale, rt = st.rot, ow = st.imgW;
      buildOverlay();
      st.x = cx; st.y = cy; st.rot = rt;
      st.scale = sc * (ow / st.imgW);               /* keep on-screen size constant across sources */
      applyTransform();
    });
    ui.toastClose.addEventListener('click', hideToast);
    ui.file.addEventListener('change', function () {
      usePhotoFile(ui.file.files && ui.file.files[0]);
      ui.file.value = '';
    });
    ui.stage.addEventListener('dragover', function (e) { e.preventDefault(); });
    ui.stage.addEventListener('drop', function (e) {
      e.preventDefault();
      usePhotoFile(e.dataTransfer.files && e.dataTransfer.files[0]);
    });

    /* pointer interaction: drag / pinch-scale / two-finger rotate with 0-deg snap */
    var pointers = new Map(), startPinch = null, lastTap = 0, lastTapX = 0, lastTapY = 0;
    ui.stage.addEventListener('pointerdown', function (e) {
      try { ui.stage.setPointerCapture(e.pointerId); } catch (err) {} /* capture is an optimization, not a requirement */
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      st.dragMoved = false;
      if (pointers.size === 2) {
        var pts = Array.from(pointers.values());
        startPinch = {
          d: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
          a: Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x),
          scale: st.scale, rot: st.rot
        };
      }
      guidesAwake();
    });
    ui.stage.addEventListener('pointermove', function (e) {
      if (!pointers.has(e.pointerId)) return;
      var prev = pointers.get(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) {
        st.x += e.clientX - prev.x;
        st.y += e.clientY - prev.y;
        st.dragMoved = true;
        applyTransform();
      } else if (pointers.size === 2 && startPinch) {
        var pts = Array.from(pointers.values());
        var dd = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        var aa = Math.atan2(pts[1].y - pts[0].y, pts[1].x - pts[0].x);
        st.scale = Math.min(4, Math.max(0.15, startPinch.scale * dd / startPinch.d));
        var rot = startPinch.rot + (aa - startPinch.a) * 180 / Math.PI;
        var norm = Math.abs(((rot % 360) + 360) % 360);
        st.rot = (norm < 3 || norm > 357) ? 0 : rot;          /* 0-deg snap */
        st.dragMoved = true;
        applyTransform();
      }
    });
    function endPointer(e) {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) startPinch = null;
      if (pointers.size === 0) {
        snapOnRelease();
        if (st.dragMoved && !st.placed) { st.placed = true; emit('place'); }
        if (!st.dragMoved && e.pointerType !== 'mouse') {     /* touch double-tap = reset (dblclick is unreliable on iOS) */
          var now = Date.now();
          if (now - lastTap < 300 && Math.hypot(e.clientX - lastTapX, e.clientY - lastTapY) < 30) { initialPlace(); lastTap = 0; }
          else { lastTap = now; lastTapX = e.clientX; lastTapY = e.clientY; }
        }
      }
    }
    ui.stage.addEventListener('pointerup', endPointer);
    ui.stage.addEventListener('pointercancel', endPointer);
    ui.stage.addEventListener('wheel', function (e) {         /* desktop: wheel = scale, Alt+wheel = rotate */
      e.preventDefault();
      if (e.altKey) {
        var rot = st.rot + (e.deltaY < 0 ? 2 : -2);
        var norm = Math.abs(((rot % 360) + 360) % 360);
        st.rot = (norm < 3 || norm > 357) ? 0 : rot;
      } else {
        st.scale = Math.min(4, Math.max(0.15, st.scale * (e.deltaY < 0 ? 1.06 : 0.94)));
      }
      applyTransform(); guidesAwake();
    }, { passive: false });
    ui.stage.addEventListener('dblclick', function () { initialPlace(); });
    ui.root.addEventListener('gesturestart', function (e) { e.preventDefault(); }); /* iOS page pinch-zoom guard */
  }

  /* ---------------- document-level lifecycle (survives Horizon morphs) ---------------- */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-sv2-ar-open]') : null;
    if (!b) return;
    e.preventDefault();
    open(b);
  });
  window.addEventListener('popstate', function () { if (st.open) close(true); });
  document.addEventListener('keydown', function (e) {
    if (!st.open) return;
    if (e.key === 'Escape') { close(false); return; }
    if (e.key === 'Tab') trapTab(e);
  });
  document.addEventListener('visibilitychange', function () {
    if (!st.open || st.mode !== 'camera') return;
    if (document.hidden) stopCamera();
    else if (!st.stream) startCamera();             /* permission already granted: allowed without a fresh gesture */
  });
  window.addEventListener('pagehide', function () { stopCamera(); });
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted || !st.open || !ui) return;
    if (!document.body.contains(ui.root)) document.body.appendChild(ui.root);   /* bfcache self-heal, body level */
    if (st.mode === 'camera' && !st.stream) startCamera();    /* iOS kills tracks in bfcache */
  });
  window.addEventListener('resize', function () { if (st.open) applyTransform(); });
})();
